import React, { useState, useEffect } from 'react';
import Helper from 'src/helper/Helper';
import { profileStore } from 'src/hooks/profile';
import Constants from 'src/helper/Constants';
import useReduxState from 'src/hooks/useReduxState';

const BillingShippingPopup = ({ SetStatus, data }) => {
    const { countryCodes, companyNamePattern, zipCodePattern, addressPattern } = Constants;
    const [{ countryList }, { get_country_list, get_state_list }] = profileStore();
    const [detailValidation, setDetailValidation] = useState({});
    const [stateList, setStateList] = useState([]);
    const [saveButtonDisable, setSaveButtonDisable] = useReduxState(true, "saveButtonDisable");

    const [updateOrderDetail, setUpdateOrderDetail] = useReduxState(
        { poNumber: '', billingAddress: {}, shippingAddress: {}, comment: '', erpPartNumbers: [], orderNumber: '' },
        'updateAdminOrderDetails'
    );

    const [billingShippingDetails, setBillingShippingDetails] = useState({
        companyName: '',
        lineOne: '',
        lineTwo: '',
        cityName: '',
        stateCode: '',
        stateName: '',
        postalCode: '',
        countryCode: '',
        countryName: '',
        addressType: '',
    });

    useEffect(() => {
        if (!countryList?.length) get_country_list();
    }, []);

    useEffect(() => {
        if (data?.addressInfo && Object.keys(data.addressInfo).length > 0) {
            const address = {
                ...data.addressInfo,
                addressType: data.type === 'Billing' ? 'BillTo' : 'ShipTo',
                stateName: data.addressInfo.stateName || '',
            };
            setBillingShippingDetails(address);

            if (Helper.valueIsNotNull(address.countryCode) && countryCodes.includes(address.countryCode)) {
                get_state_list(address.countryCode).then(setStateList);
            }
        }
    }, [data?.addressInfo]);

    const onValueChange = (field, value) => {
        setBillingShippingDetails(prev => ({ ...prev, [field]: value }));

        if (detailValidation[field]) {
            setDetailValidation(prev => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    const onCountryChange = async (event) => {
        const countryCode = event.target.value;
        const countryName = event.target.options[event.target.selectedIndex].text;

        setBillingShippingDetails(prev => ({
            ...prev,
            countryCode,
            countryName,
            stateCode: '',
            stateName: '',
        }));

        if (detailValidation.countryCode) {
            setDetailValidation(prev => {
                const updated = { ...prev };
                delete updated.countryCode;
                return updated;
            });
        }

        if (countryCodes.includes(countryCode)) {
            const states = await get_state_list(countryCode);
            setStateList(states);
        } else {
            setStateList([]);
        }
    };

    const onStateChange = (e) => {
        const stateCode = e.target.value;
        const stateName = e.target.options[e.target.selectedIndex].text;

        setBillingShippingDetails(prev => ({ ...prev, stateCode, stateName }));

        if (detailValidation['stateCode']) {
            setDetailValidation(prev => {
                const updated = { ...prev };
                delete updated['stateCode'];
                return updated;
            });
        }
    };

    const onValidateData = () => {
        const validation = {};
        const { companyName, postalCode, stateCode, lineOne, lineTwo, cityName, countryCode } = billingShippingDetails;

        if (!Helper.valueIsNotNull(companyName) || !Helper.validateRegex(companyName, companyNamePattern) || companyName.length > 100) {
            validation.companyName = 'A valid company name is required.';
        }

        if (!Helper.valueIsNotNull(postalCode) || !Helper.validateRegex(postalCode, zipCodePattern) || postalCode.length > 40) {
            validation.postalCode = 'A valid zip code is required.';
        }

        if (countryCodes.includes(countryCode) && !Helper.valueIsNotNull(stateCode)) {
            validation.stateCode = 'A valid State/Province is required.';
        }

        if (!Helper.valueIsNotNull(lineOne) || !Helper.validateRegex(lineOne, addressPattern)) {
            validation.lineOne = 'A valid address is required.';
        }

        if (lineTwo && !Helper.validateRegex(lineTwo, addressPattern)) {
            validation.lineTwo = 'A valid address is required.';
        }

        if (!Helper.valueIsNotNull(cityName) || !Helper.validateRegex(cityName, addressPattern)) {
            validation.cityName = 'A valid city is required.';
        }

        if (!Helper.valueIsNotNull(countryCode)) {
            validation.countryCode = 'A valid country is required.';
        }

        setDetailValidation(validation);
        return Object.keys(validation).length === 0;
    };


    const onSaveAddress = async () => {
        let isValid = onValidateData();
        if (isValid) {
            if (data.type === 'Billing') {
                setUpdateOrderDetail({ ...updateOrderDetail, billingAddress: billingShippingDetails, isBillingAddressEdited: true });
            } else {
                setUpdateOrderDetail({ ...updateOrderDetail, shippingAddress: billingShippingDetails, isShippingAddressEdited: true });
            }
            setSaveButtonDisable(false);
            SetStatus(false);
        }
    };

    return (
        <div className="avnet-modal modal-730">
            <div className="avnet-modal-inner">
                <button type="button" className="close" onClick={() => SetStatus(false)}>
                    ×
                </button>
                <div className="modal-header justify-content-center">
                    <h1 className="pcn-modal-heading">Edit {data.type} Address</h1>
                </div>
                <div className="modal-body">
                    <form>
                        {/* Company Name */}
                        <div className="form-group">
                            <label>Company Name*</label>
                            <input
                                id="companyName"
                                className={`form-control form-control-sm ${detailValidation.companyName ? 'error' : ''}`}
                                value={billingShippingDetails.companyName}
                                onChange={(e) => onValueChange('companyName', e.target.value)}
                                maxLength={35}
                            />
                            {detailValidation.companyName && <p className="error">{detailValidation.companyName}</p>}
                        </div>

                        {/* Address Line 1 */}
                        <div className="form-group">
                            <label>Address 1*</label>
                            <input
                                id="lineOne"
                                className={`form-control form-control-sm ${detailValidation.lineOne ? 'error' : ''}`}
                                value={billingShippingDetails.lineOne}
                                onChange={(e) => onValueChange('lineOne', e.target.value)}
                                maxLength={35}
                            />
                            {detailValidation.lineOne && <p className="error">{detailValidation.lineOne}</p>}
                        </div>

                        {/* Address Line 2 */}
                        <div className="form-group">
                            <label>Address 2</label>
                            <input
                                id="lineTwo"
                                className={`form-control form-control-sm ${detailValidation.lineTwo ? 'error' : ''}`}
                                value={billingShippingDetails.lineTwo}
                                onChange={(e) => onValueChange('lineTwo', e.target.value)}
                                maxLength={35}
                            />
                            {detailValidation.lineTwo && <p className="error">{detailValidation.lineTwo}</p>}
                        </div>

                        {/* City */}
                        <div className="form-group">
                            <label>City*</label>
                            <input
                                id="cityName"
                                className={`form-control form-control-sm ${detailValidation.cityName ? 'error' : ''}`}
                                value={billingShippingDetails.cityName}
                                onChange={(e) => onValueChange('cityName', e.target.value)}
                            />
                            {detailValidation.cityName && <p className="error">{detailValidation.cityName}</p>}
                        </div>

                        {/* Country */}
                        <div className="form-group">
                            <label>Country*</label>
                            <div className="selectbox">
                                <select
                                    id="countryCode"
                                    className={`form-control form-control-sm ${detailValidation.countryCode ? 'error' : ''}`}
                                    value={billingShippingDetails.countryCode}
                                    onChange={onCountryChange}
                                >
                                    <option value="">Enter Country</option>
                                    {countryList?.map((country) => (
                                        <option key={country.countryId} value={country.countryCode}>
                                            {country.countryName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {detailValidation.countryCode && <p className="error">{detailValidation.countryCode}</p>}
                        </div>

                        {/* State & Zip */}
                        <div className="bs5 row form-group">
                            <div className="col-md-6">
                                <label>State/Province{stateList.length ? '*' : ''}</label>
                                <div>
                                    {stateList.length > 0 ? (
                                        <div className="selectbox">
                                            <select
                                                id="stateCode"
                                                className={`form-control form-control-sm ${detailValidation.stateCode ? 'error' : ''}`}
                                                value={billingShippingDetails.stateCode}
                                                onChange={onStateChange}
                                            >
                                                <option value="">-Select-</option>
                                                {stateList.map((state) => (
                                                    <option key={state.stateId} value={state.stateCode}>
                                                        {state.stateName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <input
                                            id="stateCode"
                                            className="form-control form-control-sm"
                                            placeholder="-Select-"
                                            value={billingShippingDetails.stateCode}
                                            onChange={(e) => onValueChange('stateCode', e.target.value)}
                                        />
                                    )}
                                    {detailValidation.stateCode && <p className="error">{detailValidation.stateCode}</p>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label>Zip/Postal Code*</label>
                                <input
                                    id="postalCode"
                                    className={`form-control form-control-sm ${detailValidation.postalCode ? 'error' : ''}`}
                                    value={billingShippingDetails.postalCode}
                                    onChange={(e) => onValueChange('postalCode', e.target.value)}
                                />
                                {detailValidation.postalCode && <p className="error">{detailValidation.postalCode}</p>}
                            </div>
                        </div>
                    </form>
                </div>

                <div className="modal-footer d-flex justify-content-center">
                    <button type="button" className="bs5 btn btn-secondary minw140" onClick={() => SetStatus(false)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="bs5 btn btn-primary minw140"
                        onClick={onSaveAddress}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillingShippingPopup;
